// Security dashboard › Security settings (wave 3, W2_Portal_Settings look).
//
// Reads `securitySettings` from the Redux settings slice (already fetched at
// sign-in; no new read) and writes each change straight away, exactly as
// before: update(ref(database, 'settings/securitySettings'), { [key]: value })
// for every switch flip and for every whole number typed (a typed value that
// is not a whole number of 0 or more is not written). Admin only.
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ref, update } from 'firebase/database';
import { FirebaseContext } from 'common';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { showToast, InlineMessage } from '../../components/feedback';
import { tokens as c } from '../../theme';
import { SectionCard, SettingRow, IconCircle } from '../../components/modern';

const DEFAULT_SETTINGS = {
  rateLimitSignupIp: true,
  rateLimitSignupPhone: true,
  rateLimitSignupRecaptcha: true,
  rateLimitCheckUser: true,
  rateLimitOtpRequestPhone: true,
  rateLimitOtpRequestIp: true,
  encryptWebResponses: true,
  otpExpiryMinutes: 5,
  otpMaxAttempts: 3,
  rateLimitOtpRequestMax: 3,
  rateLimitOtpRequestWindow: 10,
  rateLimitOtpRequestIpMax: 10,
  rateLimitOtpRequestIpWindow: 60,
  rateLimitSignupMax: 50,
  rateLimitSignupWindow: 60,
  rateLimitSignupIpMax: 50,
  rateLimitSignupIpWindow: 60,
  rateLimitCheckUserMax: 50,
  rateLimitCheckUserWindow: 60,
};

// A whole-number setting. The box keeps what is typed (so it can be cleared
// and retyped); each whole number of 0 or more is written at once, as before.
function NumberSetting({ id, label, hint, value, fallback, min, max, disabled, onValue }) {
  const shown = String(value || fallback);
  const [text, setText] = useState(shown);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(shown);
  }, [shown, focused]);
  return (
    <SettingRow
      id={id}
      label={label}
      hint={hint}
      disabled={disabled}
      control={
        <TextField
          id={id}
          size="small"
          type="number"
          value={text}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setText(shown);
          }}
          onChange={(e) => {
            setText(e.target.value);
            onValue(e.target.value);
          }}
          inputProps={{ min, max, inputMode: 'numeric', 'aria-describedby': hint ? `${id}-hint` : undefined }}
          sx={{ width: { xs: '100%', sm: 160 }, '& input': { fontVariantNumeric: 'tabular-nums' } }}
        />
      }
    />
  );
}

export default function SecuritySettings() {
  const { t, i18n } = useTranslation();
  const { database } = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const settingsData = useSelector((state) => state.settingsdata);
  const isAdmin = auth?.profile?.usertype === 'admin';

  const [securitySettings, setSecuritySettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    if (settingsData && settingsData.settings && settingsData.settings.securitySettings) {
      setSecuritySettings(settingsData.settings.securitySettings);
    }
  }, [settingsData]);

  const failed = (error) => {
    console.error('Failed to update security setting:', error);
    showToast.error(t('update_failed', { defaultValue: 'Update failed' }));
  };

  const handleSettingChange = async (setting) => {
    if (!database) return;
    const newValue = !securitySettings[setting];
    const updatedSettings = { ...securitySettings, [setting]: newValue };
    setSecuritySettings(updatedSettings);
    try {
      const settingsRef = ref(database, 'settings/securitySettings');
      await update(settingsRef, { [setting]: newValue });
    } catch (error) {
      failed(error);
      setSecuritySettings(securitySettings);
    }
  };

  const handleNumericSettingChange = async (setting, value) => {
    if (!database) return;
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0) return;
    const updatedSettings = { ...securitySettings, [setting]: numValue };
    setSecuritySettings(updatedSettings);
    try {
      const settingsRef = ref(database, 'settings/securitySettings');
      await update(settingsRef, { [setting]: numValue });
    } catch (error) {
      failed(error);
      setSecuritySettings(securitySettings);
    }
  };

  if (!isAdmin) {
    return <InlineMessage kind="warning">{t('admin_access_required', { defaultValue: 'Admin access required.' })}</InlineMessage>;
  }

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
  const maxRequests = L('sec_max_requests', 'Max requests');
  const windowMinutes = L('sec_window_minutes', 'Window (minutes)');

  const toggle = (key, label, hint) => (
    <SettingRow
      id={`sec-${key}`}
      layout="switch"
      label={label}
      hint={hint}
      control={
        <Switch
          id={`sec-${key}`}
          checked={securitySettings[key] || false}
          onChange={() => handleSettingChange(key)}
          inputProps={hint ? { 'aria-describedby': `sec-${key}-hint` } : undefined}
          sx={{ my: -0.75 }}
        />
      }
    />
  );

  const number = (key, label, fallback, min, max, disabled, hint) => (
    <NumberSetting
      id={`sec-${key}`}
      label={label}
      hint={hint}
      value={securitySettings[key]}
      fallback={fallback}
      min={min}
      max={max}
      disabled={disabled}
      onValue={(v) => handleNumericSettingChange(key, v)}
    />
  );

  // Sub-settings sit indented under the switch they belong to.
  const nested = (children) => <Box sx={{ pl: { xs: 1.5, sm: 3 }, borderInlineStart: `2px solid ${c.OUTLINE}`, mb: 1 }}>{children}</Box>;

  const statusItems = [
    L('sec_status_otp', 'OTP validation: enhanced'),
    L('sec_status_storage', 'Storage rules: secured'),
    L('sec_status_database', 'Database rules: updated'),
    L('sec_status_booking', 'Booking auth: enforced'),
    L('sec_status_claims', 'Admin claims: synced'),
    L('sec_status_cleanup', 'Security cleanup: scheduled'),
  ];

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'start' }}>
        {/* OTP configuration */}
        <SectionCard id="sec-otp" icon={<VpnKeyOutlinedIcon />} hue="info" title={L('sec_otp_title', 'OTP configuration')}>
          <Box sx={{ mt: 1 }}>
            {number('otpExpiryMinutes', L('sec_otp_expiry', 'OTP expiry (minutes)'), 5, 1, 60, false, L('sec_otp_expiry_hint', 'How long OTP codes remain valid'))}
            {number('otpMaxAttempts', L('sec_otp_attempts', 'Max OTP attempts'), 3, 1, 10, false, L('sec_otp_attempts_hint', 'Maximum verification attempts before the OTP expires'))}
            {toggle('rateLimitOtpRequestPhone', L('sec_otp_limit_phone', 'Rate limit OTP requests by phone'))}
            {nested(
              <>
                {number('rateLimitOtpRequestMax', maxRequests, 3, 1, 100, !securitySettings.rateLimitOtpRequestPhone)}
                {number('rateLimitOtpRequestWindow', windowMinutes, 10, 1, 1440, !securitySettings.rateLimitOtpRequestPhone)}
              </>
            )}
            {toggle('rateLimitOtpRequestIp', L('sec_otp_limit_ip', 'Rate limit OTP requests by IP'))}
            {nested(
              <>
                {number('rateLimitOtpRequestIpMax', maxRequests, 10, 1, 1000, !securitySettings.rateLimitOtpRequestIp)}
                {number('rateLimitOtpRequestIpWindow', windowMinutes, 60, 1, 1440, !securitySettings.rateLimitOtpRequestIp)}
              </>
            )}
          </Box>
        </SectionCard>

        {/* Signup rate limiting */}
        <SectionCard id="sec-signup" icon={<SpeedOutlinedIcon />} hue="amber" title={L('sec_signup_title', 'Sign-up rate limiting')}>
          <Box sx={{ mt: 1 }}>
            {toggle('rateLimitSignupIp', L('sec_signup_limit_ip', 'Rate limit sign-ups by IP address'))}
            {nested(
              <>
                {number('rateLimitSignupIpMax', L('sec_max_signups', 'Max sign-ups'), 50, 1, 1000, !securitySettings.rateLimitSignupIp)}
                {number('rateLimitSignupIpWindow', windowMinutes, 60, 1, 1440, !securitySettings.rateLimitSignupIp)}
              </>
            )}
            {toggle('rateLimitSignupPhone', L('sec_signup_limit_phone', 'Rate limit sign-ups by phone number'))}
            {nested(
              <>
                {number('rateLimitSignupMax', L('sec_max_signups', 'Max sign-ups'), 50, 1, 1000, !securitySettings.rateLimitSignupPhone)}
                {number('rateLimitSignupWindow', windowMinutes, 60, 1, 1440, !securitySettings.rateLimitSignupPhone)}
              </>
            )}
            {toggle('rateLimitSignupRecaptcha', L('sec_signup_recaptcha', 'reCAPTCHA check on sign-up'), L('sec_signup_recaptcha_hint', 'Needs reCAPTCHA v3 keys to be configured'))}
          </Box>
        </SectionCard>

        {/* Check-user rate limiting and encryption */}
        <SectionCard id="sec-check" icon={<SecurityOutlinedIcon />} hue="accent" title={L('sec_check_title', 'User check rate limiting')}>
          <Box sx={{ mt: 1 }}>
            {toggle('rateLimitCheckUser', L('sec_check_limit', 'Rate limit user existence checks'))}
            {nested(
              <>
                {number('rateLimitCheckUserMax', L('sec_max_checks', 'Max checks'), 50, 1, 1000, !securitySettings.rateLimitCheckUser)}
                {number('rateLimitCheckUserWindow', windowMinutes, 60, 1, 1440, !securitySettings.rateLimitCheckUser)}
              </>
            )}
            <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, color: c.INK, pt: 2, pb: 0.5 }}>
              {L('sec_encryption_title', 'Encryption')}
            </Typography>
            {toggle('encryptWebResponses', L('sec_encrypt', 'Encrypt sensitive API responses'), L('sec_encrypt_hint', 'Encrypts user data in check_user_exists and other sensitive endpoints'))}
          </Box>
        </SectionCard>

        {/* Security status */}
        <Paper component="section" aria-labelledby="sec-status-title" sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
            <IconCircle icon={<VerifiedUserOutlinedIcon />} hue="success" size={34} />
            <Typography id="sec-status-title" component="h2" sx={{ fontSize: 17, fontWeight: 700, color: c.INK }}>
              {L('sec_status_title', 'Security status')}
            </Typography>
          </Box>
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 1.25, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
            {statusItems.map((label) => (
              <Box component="li" key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14.5, color: c.INK }}>
                <CheckCircleRoundedIcon aria-hidden="true" sx={{ color: c.SUCCESS_TEXT, fontSize: 20 }} />
                {label}
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
